import React, { useEffect, useState } from "react";
import { X, Download, Smartphone, Share2 } from "lucide-react";

/**
 * Phase 25.5 — PWA install prompt.
 *
 * Shows a small bottom-sheet banner the first time we detect the
 * `beforeinstallprompt` event (Chrome/Edge on Android, desktop Chrome).
 * Safari/iOS doesn't fire the event — for those users we show a tutorial
 * card explaining the "Add to Home Screen" gesture once.
 *
 * Dismissals are persisted in localStorage so we don't nag.
 */

const DISMISS_KEY = "dara_pwa_install_dismissed_at";
const DISMISS_TTL_DAYS = 30;

function isIOSStandalone() {
  return (
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.matchMedia("(display-mode: standalone)").matches &&
    !window.navigator.standalone
  );
}

function isAlreadyInstalled() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isRecentlyDismissed() {
  if (typeof localStorage === "undefined") return false;
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  const days = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
  return days < DISMISS_TTL_DAYS;
}

export default function PWAInstallPrompt() {
  const [installEvent, setInstallEvent] = useState(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isAlreadyInstalled() || isRecentlyDismissed()) return;

    // Chrome / Edge / Android: beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      // Defer showing the banner until the user has spent a bit on the page
      setTimeout(() => setShow(true), 8000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari fallback — show the manual tutorial after 12 sec
    if (isIOSStandalone()) {
      setTimeout(() => {
        if (!isRecentlyDismissed()) {
          setShowIosTip(true);
          setShow(true);
        }
      }, 12000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "accepted") {
      setShow(false);
    } else {
      dismiss();
    }
    setInstallEvent(null);
  };

  const dismiss = () => {
    setShow(false);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto bg-black border border-[#FFB000]/40 shadow-2xl animate-in slide-in-from-bottom"
      data-testid="pwa-install-prompt"
      style={{ animation: "slide-up 0.3s ease-out" }}
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-zinc-500 hover:text-white p-1"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="p-4 pr-9">
        <div className="flex items-start gap-3">
          <div className="border border-[#FFB000]/40 bg-[#FFB000]/10 p-2.5 flex-shrink-0">
            <Smartphone className="w-5 h-5 text-[#FFB000]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#FFB000] mb-1">
              // INSTALL DARA
            </div>
            <h3 className="font-heading font-bold text-base text-white leading-tight">
              Run DARA like a native app
            </h3>
            {showIosTip ? (
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                Tap <Share2 className="w-3 h-3 inline mb-0.5" /> Share at the bottom of Safari,
                then <strong className="text-white">Add to Home Screen</strong>. DARA opens
                full-screen with a black splash and the gold logo — same as Spotify or X.
              </p>
            ) : (
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                Add DARA to your home screen — opens full-screen, instant launch, perfect
                for checking POS Live from the shop floor.
              </p>
            )}
          </div>
        </div>

        {!showIosTip && installEvent && (
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="mt-3 w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:opacity-90 inline-flex items-center justify-center gap-2"
          >
            <Download className="w-3.5 h-3.5" />
            INSTALL APP
          </button>
        )}
        {showIosTip && (
          <button
            onClick={dismiss}
            data-testid="pwa-install-ios-got-it"
            className="mt-3 w-full border border-zinc-800 hover:border-zinc-700 font-mono text-[11px] uppercase tracking-widest text-zinc-300 px-4 py-2.5"
          >
            GOT IT
          </button>
        )}
      </div>
    </div>
  );
}
